import * as Yup from "yup";

import {
  AddReinsurersParticipationPercentage,
  AddReinsurersParticipationPercentageVariables,
} from "_graphql/mutation/__generated__/AddReinsurersParticipationPercentage";
import { useCallback, useEffect, useMemo } from "react";
import { useMatch, useSearch } from "react-location";
import { useTreaty, useTreatyReinsurer } from "hooks/data/use-treaties";

import { LocationGenerics } from "router/location";
import { Modal } from "components";
import { PercentageForm } from "components/forms";
import _ from "lodash";
import toast from "react-hot-toast";
import { updateTreatyReinsurerPercentage } from "_graphql/visal/mutations/treaty";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

export const validationSchema = Yup.object().shape({
  expected: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number"),

  percentage: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Percentage is required")
    .test(
      "percentage",
      ({ parent }) =>
        `Percentage must be less than or equal to remaining percentage: ${parent?.expected}`,
      function (value) {
        const { expected } = this.parent;
        if (expected == null || value == null) return true;
        return value <= expected;
      }
    ),
});

export default function AddParticipantsPercentageContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const { params } = useMatch<LocationGenerics>();
  const searchParams = useSearch<LocationGenerics>();
  const { treaty, loading: fetchingTreaty } = useTreaty({
    treatyId: params.treaty || "",
  });
  const { participant, loading: fetching } = useTreatyReinsurer({
    participationId: searchParams.id || "",
  });

  const [create, { loading }] = useMutation<
    AddReinsurersParticipationPercentage,
    AddReinsurersParticipationPercentageVariables
  >(updateTreatyReinsurerPercentage, {
    refetchQueries: ["GetTreatyReinsurerParticipants", "Treaty"],
  });


  const getProportionalPercentage = useCallback(
    (totalPercentage: number) => {
      const participantPercentage = participant?.treaty_participation_percentage || 0;
      return (totalPercentage + (participant?.treaty_participation_percentage || 0)) -
        [
          ...(treaty?.treaty_participants?.filter(
            (el) => el?.treaty_participation_id !== searchParams.id
          ) || []),
        ]?.reduce(
          (acc, _participant) => {
            return acc + (_participant?.treaty_participation_percentage || 0)
          },
          0
        ) || 0
    }
    , [treaty?.treaty_participants, searchParams.id, participant?.treaty_participation_percentage]);

  const getNonProportionalPercentage = useCallback((totalPercentage_: number) => {
    const groupedItems = _.groupBy(
      treaty?.treaty_participants || [],
      "layer_number"
    );
    const totalAssigned =
      _.get(groupedItems, searchParams?.layer || 1)
        ?.filter((el) => el?.treaty_participation_id !== searchParams.id)
        ?.reduce(
          (acc, item) => acc + (item?.treaty_participation_percentage || 0),
          0
        ) || 0;
    return (totalPercentage_ + Number(participant?.treaty_participation_percentage || 0)) - totalAssigned;
  }, [treaty?.treaty_participants, searchParams?.layer, searchParams?.id, participant?.treaty_participation_percentage]);

  const totalPercentage = treaty?.order_hereon || 100;
  const remainingPercentage = useMemo(
    () =>
      searchParams?.treaty_type === "PROPORTIONAL"
        ? getProportionalPercentage(totalPercentage)
        : getNonProportionalPercentage(totalPercentage),
    [
      searchParams?.treaty_type,
      treaty?.order_hereon,
      treaty?.treaty_participants,
      totalPercentage,
      searchParams?.id,
      getNonProportionalPercentage,
      getProportionalPercentage
    ]
  );

  const form = useFormik<any>({
    initialValues: {
      percentage: participant?.treaty_participation_percentage || 0,
      expected: remainingPercentage,
    },
    validationSchema: validationSchema,
    onSubmit: async (variables) => {
      await create({
        variables: {
          treatyId: params.treaty || "",
          treatyParticipationId: searchParams.id?.toString() || "",
          layerNumber: Number(searchParams.layer),
          participationPercentage: variables.percentage,
          associateDeductionId:
            treaty?.treaty_deduction?.treaty_associate_deduction_id || "",
        },
      }).then(({ data }) => {
        if (data?.addReinsurersParticipationPercentage) {
          toast.success("Percentage added successfully");
          refetch?.();
          setOpen?.(false);
          form.resetForm();
        } else {
          toast.error("Failed to add percentage");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });



  useEffect(() => {
    if (remainingPercentage !== form.values?.expected) {
      form.setFieldValue("expected", remainingPercentage);
    }
  }, [remainingPercentage, form.values?.expected]);

  useEffect(() => {
    if (participant && participant.treaty_participation_percentage !== form.values.percentage) {
      form.setFieldValue(
        "percentage",
        participant.treaty_participation_percentage
      );
    }
  }, [participant]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={"Add Percentage to " + participant?.reinsurer?.re_company_name}
      loading={fetching || fetchingTreaty}
      description="Add percentage for this ReInsurer"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Saving changes..." : "Save changes"}
          </button>
        </>
      )}
    >
      <PercentageForm form={form} />
    </Modal>
  );
}
